import { useEffect, useMemo, useState } from 'react'
import { BarChart3, CheckCircle, Clock, FileText, Upload } from 'lucide-react'
import { documentsApi, type Document } from '../api'
import DocumentList from '../components/DocumentList'
import FileUpload from '../components/FileUpload'
import StatsCard from '../components/StatsCard'
import SubscriptionNotice from '../components/SubscriptionNotice'
import UsageMeter from '../components/UsageMeter'
import { useAuthStore } from '../store'

export default function Dashboard() {
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const user = useAuthStore((state) => state.user)

  const loadDocuments = async () => {
    try {
      setLoading(true)
      const response = await documentsApi.list()
      setDocuments(response.data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDocuments()
  }, [])

  const stats = useMemo(() => ({
    total: documents.length,
    active: documents.filter((doc) => ['queued', 'processing', 'exporting'].includes(doc.status)).length,
    completed: documents.filter((doc) => ['processed', 'export_ready'].includes(doc.status)).length,
    storage: documents.reduce((sum, doc) => sum + doc.file_size, 0),
  }), [documents])

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col gap-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-950">Documents</h1>
          <p className="text-gray-600 mt-1">Signed in as {user?.email}</p>
        </div>
        <button onClick={loadDocuments} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
          Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 shrink-0">
        <StatsCard title="Total Documents" value={stats.total} icon={FileText} color="blue" />
        <StatsCard title="Active Jobs" value={stats.active} icon={Clock} color="yellow" />
        <StatsCard title="Processed" value={stats.completed} icon={CheckCircle} color="green" />
        <StatsCard title="Storage Used" value={formatBytes(stats.storage)} icon={BarChart3} color="purple" />
      </div>

      <div className="shrink-0">
        <UsageMeter compact />
      </div>

      <SubscriptionNotice context="dashboard" className="shrink-0" />

      <section className="bg-white border border-gray-200 rounded-lg shrink-0">
        <div className="p-6 border-b border-gray-200 flex items-center gap-3">
          <Upload className="w-5 h-5 text-brand-blue" />
          <div>
            <h2 className="font-semibold text-gray-950">Upload PDF</h2>
            <p className="text-sm text-gray-600">Duplicate files are detected with SHA256.</p>
          </div>
        </div>
        <div className="p-6">
          <SubscriptionNotice context="upload" className="mb-4" />
          <FileUpload onUploadComplete={loadDocuments} />
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-lg min-h-0 flex-1 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <h2 className="font-semibold text-gray-950">Document Operations</h2>
        </div>
        <div className="min-h-0 overflow-auto">
          {loading ? (
          <div className="p-12 text-center text-gray-500">Loading documents...</div>
          ) : (
          <DocumentList documents={documents} onDelete={loadDocuments} onRefresh={loadDocuments} />
          )}
        </div>
      </section>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B'
  const sizes = ['B', 'KB', 'MB', 'GB']
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1)
  return `${(bytes / Math.pow(1024, index)).toFixed(1)} ${sizes[index]}`
}
